import type { Control, FieldErrors } from 'react-hook-form'
import type { VoucherFormValues } from './voucher-form-schema'
import type { VoucherStatus } from '@/entities/voucher'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { SectionHeader } from '@/shared/ui/section-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { DateInput } from './date-input'

interface VoucherBasicSectionProps {
  control: Control<VoucherFormValues>
  errors: FieldErrors<VoucherFormValues>
  mode: 'create' | 'edit'
}

export function VoucherBasicSection({ control, errors, mode }: VoucherBasicSectionProps) {
  const { t } = useTranslation()
  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  return (
    <div>
      <SectionHeader
        title={t('voucher.sectionBasic')}
        description={t('voucher.sectionBasicSubtitle')}
      />
      <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
        <FormField label={t('voucher.code')} required error={getErrorText(errors.code?.message)}>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="VD: TOUR10, POI20..."
                disabled={mode === 'edit'}
                className="font-mono"
              />
            )}
          />
        </FormField>

        <FormField label={t('voucher.name')} required error={getErrorText(errors.name?.message)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('voucher.namePlaceholder')}
              />
            )}
          />
        </FormField>

        <FormField
          label={t('voucher.discountPercent')}
          required
          error={getErrorText(errors.discountPercent?.message)}
        >
          <div className="relative">
            <Controller
              name="discountPercent"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  onBlur={field.onBlur}
                  className="pr-8"
                />
              )}
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-zinc-400 text-sm">
              %
            </span>
          </div>
        </FormField>

        <FormField
          label={t('voucher.quantity')}
          required
          error={getErrorText(errors.quantity?.message)}
        >
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => (
              <Input
                type="number"
                min={1}
                value={field.value}
                onChange={(e) => field.onChange(e.target.valueAsNumber)}
                onBlur={field.onBlur}
              />
            )}
          />
        </FormField>

        <FormField
          label={t('voucher.startDate')}
          required
          error={getErrorText(errors.startDate?.message)}
        >
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <DateInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
            )}
          />
        </FormField>

        <FormField
          label={t('voucher.endDate')}
          required
          error={getErrorText(errors.endDate?.message)}
        >
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <DateInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
            )}
          />
        </FormField>

        <FormField label={t('common.status')} required>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={(v: VoucherStatus) => field.onChange(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AVAILABLE">{t('voucher.status.available')}</SelectItem>
                  <SelectItem value="UNAVAILABLE">{t('voucher.status.unavailable')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormField>
      </div>
    </div>
  )
}
